<template>
  <div class="photo-editor-wrapper">
    <IQOSModal size="photoeditor">
      <template #title>
        <div class="editor-titles">
          <h4 class="qrw-heading-xs v1-grey-2-color bold">
            {{ $t("dish_photo_editor.title") }}
          </h4>
          <!-- <h4 class="qrw-heading-xs v1-grey-6-color">
            {{ $t("dish_photo_editor.hint") }}
          </h4> -->
        </div>
      </template>
      <div class="editor-area gap-row-sm">
        <BaseTabSwitcher
          :value="selectedFormat"
          @input="chooseFormat"
          class="w-100"
          theme="dark"
          :tabs="[
            {
              text: $t('dish_photo_editor.format_2'),
              value: 'wide',
            },
            {
              text: $t('dish_photo_editor.format_1'),
              value: 'standart',
            },
          ]"
        />

        <div class="image-area gap-row-xs">
          <div
            class="canvas-container"
            @mousedown.prevent="isGrabbed = true"
            @mouseup.prevent="isGrabbed = false"
            @mousemove="onMoving"
            @mouseleave="isGrabbed = false"
          >
            <PhotoEditorCanvas
              @imageUpdated="onCanvasImageUpdated"
              @present="(p) => (canvasPresentation = p)"
              ref="canvasContainer"
              :transformations="pipeline || getDefaultPipeline()"
              :frameSize="frameSizes[selectedFormat]"
              :image="currentImageUrl"
              :style="{
                visibility: canvasReady ? 'visible' : 'hidden',
              }"
            />
          </div>
        </div>

        <BaseRangeInput
          v-model="tempZoomValue"
          @change="handleTempZoomValueChange"
          :min="zoomRanges[selectedFormat][0] || 0"
          :max="zoomRanges[selectedFormat][1] || 200"
          lineColor="grey"
          pointerColor="white"
        />

        <!-- <BaseRangeInput
          v-model="pipeline.rotate"
          :min="0"
          :max="360"
          lineColor="grey"
          pointerColor="white"
        /> -->
        <!-- todo fix -->

        <!-- <div class="qrw-paragraph-md v1-grey-1-color">
          {{
            `x: ${pipeline.translate_x}, y: ${pipeline.translate_y}, zoom: ${pipeline.zoom}, rotate: ${pipeline.rotate}`
          }}
          <br />
          {{
            `present x: (${(canvasPresentation.present_x || 0).toFixed(2)}, ${(
              canvasPresentation.present_y || 0
            ).toFixed(2)}), corner: (${(
              canvasPresentation.corner_x || 0
            ).toFixed(2)}, ${(canvasPresentation.corner_y || 0).toFixed(
              2
            )}), first: (${(canvasPresentation.first_x || 0).toFixed(2)}, ${(
              canvasPresentation.first_y || 0
            ).toFixed(2)}) frame x: ${canvasPresentation.frame_x}, frame y: ${
              canvasPresentation.frame_y
            }, width: ${(canvasPresentation.present_width || 0).toFixed(
              2
            )}, height: ${(canvasPresentation.present_height || 0).toFixed(2)}`
          }}
        </div> -->

        <div class="editor-modal-bottom gap-row-xs">
          <BaseDivider color="v1-grey-8" />

          <div class="editor-actions gap-column-xs">
            <div class="image-actions-area gap-column-1xs w-100">
              <ImageUploader image_type="editor" @success="onNewImageUploaded">
                <BaseButton
                  icon="upload"
                  class="editor-image-action"
                  variant="secondary"
                  size="md"
                  color="v1-grey-6"
                />
              </ImageUploader>
              <BaseButton
                icon="scale"
                variant="secondary"
                size="md"
                class="editor-image-action"
                color="v1-grey-6"
                @click="fitPhoto"
              />

              <!-- <BaseButton
                @click="onRotate('right')"
                icon="rotate-right"
                class="editor-image-action"
                ref="rotate"
                variant="secondary"
                size="md"
                color="v1-grey-6"
              />
              <BaseButton
                @click="onRotate('left')"
                icon="rotate-left"
                ref="rotate"
                variant="secondary"
                class="editor-image-action"
                size="md"
                color="v1-grey-6"
              /> -->
            </div>

            <div class="gap-column-1xs">
              <BaseButton
                @click="$emit('close')"
                variant="secondary"
                color="v1-grey-6"
                size="md"
                class="editor-modal-button"
                :busy="busy"
                >{{ $t("dish_photo_editor.cancel") }}
              </BaseButton>
              <BaseButton
                variant="primary"
                color="green-6"
                size="md"
                class="editor-modal-button"
                icon="check"
                :busy="busy"
                @click="onConfirm"
                >{{ $t("dish_photo_editor.button_save") }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </IQOSModal>
  </div>
</template>

<script>
import ImageUploader from "@/components/ImageUploader.vue";
import MediaAPI from "@/api/media";

import PhotoEditorCanvas from "@/components/modals/PhotoEditorCanvas.vue";

import {
  scale,
  rotateDEG,
  translate,
  compose,
  applyToPoint,
  fromObject,
  inverse,
} from "transformation-matrix";
import { decomposeTSR } from "transformation-matrix";

const lerp = (x, y, a) => x * (1 - a) + y * a;

function _interp(value1, value2, amount) {
  amount = amount < 0 ? 0 : amount;
  amount = amount > 1 ? 1 : amount;
  return value1 + (value2 - value1) * amount;
}

function interpolate(a, b, frac) {
  return {
    x: _interp(a.x, b.x, frac),
    y: _interp(a.y, b.y, frac),
  };
}

const clamp = (a, min = 0, max = 1) => Math.min(max, Math.max(min, a));
const invlerp = (x, y, a) => clamp((a - x) / (y - x));

export default {
  data() {
    return {
      pipeline: {
        zoom: 1,
        rotate: 0,
        translate_x: 0,
        translate_y: 0,

        relative_x: 0,
        relative_y: 0,
      },
      saved_formats: {
        standart: null,
        wide: null,
        ultrawide: null,
      },
      isGrabbed: false,
      selectedFormat: "standart",
      currentImageUrl: null,

      tempZoomValue: 100,
      lastPipelineZooom: 1,

      nextValidZoomCoeficient: 1,

      busy: false,

      frameSizes: {
        standart: [300, 300],
        wide: [534, 300],
        ultrawide: [710, 200],
      },

      zoomRanges: {
        standart: [],
        wide: [],
      },

      fitPositions: {
        standart: {},
        wide: {},
      },

      cached_image_size: {
        x: 0,
        y: 0,
      },

      cachedPipelines: {
        standart: null,
        wide: null,
        ultrawide: null,
      },

      firstLoad: true,

      canvasReady: false,

      lastMovedZoomValue: 100,

      canvasPresentation: {},

      dirtyTempZoomValue: false,
    };
  },
  components: {
    ImageUploader,
    PhotoEditorCanvas,
  },
  props: {
    image_url: String,
  },
  watch: {
    tempZoomValue(new_value, old_value) {
      if (this.dirtyTempZoomValue) {
        this.dirtyTempZoomValue = false;
        return;
      }

      let dir = new_value > old_value ? 1 : -1;

      let ptr = Math.round(old_value);

      if (new_value == old_value) dir = 0;

      if (Math.abs(new_value / 100, this.nextValidZoomCoeficient) <= 0.01)
        return;

      let diff = new_value - old_value;

      while (Math.abs(ptr - Math.round(new_value)) > 0.1) {
        // console.log(`new ptr: ${ptr} vs ${new_value}`)
        if (!this.updateZoom(ptr, diff)) return;
        ptr += dir;
      }

      this.updateZoom(ptr, diff);

      this.lastMovedZoomValue = this.pipeline.zoom;
    },
    currentImageUrl() {
      // this.$nextTick(this.updateImageSize);
      // this.$nextTick(this.initialFit);
    },
  },
  methods: {
    onCanvasImageUpdated() {
      this.initialFit();
    },
    getDefaultPipeline() {
      return {
        rotate: 0,
        zoom: 1,
        translate_y: 0,
        translate_x: 0,
        relative_x: 0,
        relative_y: 0,
      };
    },
    handleTempZoomValueChange() {
      this.dirtyTempZoomValue = true;
      this.tempZoomValue = this.nextValidZoomCoeficient * 100;

      const [x_valid, y_valid] = this.checkCropping();

      if (!x_valid || !y_valid) {
        this.setNearestValidImagePosition();
      }
    },
    updateZoom(newRangeValue, diff) {
      const newZoomValue = (this.lastPipelineZooom * newRangeValue) / 100;

      const targetZoom = this.zoomRanges[this.selectedFormat][0] / 100;

      const tempPresentation = this.$refs.canvasContainer.computePresentation(
        this.pipeline.translate_x,
        this.pipeline.translate_y,
        newZoomValue,
        this.pipeline.rotate
      );

      const [x_valid, y_valid] = this.checkCropping(0, 0, tempPresentation);

      let canApplyZoom = true;

      // if (x_valid && y_valid) {
      //   canApplyZoom = true;
      // }

      // if (!canApplyZoom) {
      //   const fitPos = this.fitPositions[this.selectedFormat];

      //   const maxValue = this.zoomRanges[this.selectedFormat][1] / 100;

      //   const interpolationSpeed =
      //     invlerp(this.lastMovedZoomValue, targetZoom, newZoomValue) * 0.3;
      //   // const interpolationSpeed = Math.abs(diff / maxValue);

      //   let newPos = {};

      //   if (fitPos.length && !isNaN(fitPos[0])) {
      //     newPos = interpolate(
      //       {
      //         x: this.pipeline.translate_x,
      //         y: this.pipeline.translate_y,
      //       },
      //       {
      //         x: fitPos[0],
      //         y: fitPos[1],
      //       },
      //       interpolationSpeed
      //     );

      //     console.log(
      //       `Interpolated to: ${newPos.x}, ${newPos.y} with speed ${interpolationSpeed} (${newZoomValue} out of ${targetZoom}, ${maxValue}`
      //     );
      //   }

      //   const recalculatedPresentation =
      //     this.$refs.canvasContainer.computePresentation(
      //       newPos.x,
      //       newPos.y,
      //       newZoomValue,
      //       this.pipeline.rotate
      //     );

      //   console.log(`------`);
      //   const [new_x_valid, new_y_valid] = this.checkCropping(
      //     0,
      //     0,
      //     recalculatedPresentation
      //   );

      //   this.pipeline.translate_y = Math.round(newPos.y);
      //   this.pipeline.translate_x = Math.round(newPos.x);

      //   if (new_x_valid && new_y_valid) {
      //     canApplyZoom = true;
      //   }
      // }

      if (canApplyZoom) {
        this.pipeline.zoom = newZoomValue;
        this.nextValidZoomCoeficient = newZoomValue;
        console.log(`Valid next value: ${newZoomValue}`);
        return true;
      } else {
        console.log(`Invalid next value: ${newZoomValue}`);
        return false;
      }
    },
    getMappedPoint() {
      const tMatrix = this.$refs.canvasContainer.computeMatrix(
        this.pipeline.translate_x,
        this.pipeline.translate_y,
        this.pipeline.zoom,
        this.pipeline.rotate
        // this.isOrientationChanged ? image_size.y : image_size.x,
        // this.isOrientationChanged ? image_size.x : image_size.y
      );

      // const inverseMatrix = inverse(tMatrix);

      const inverseMatrix = inverse(tMatrix);

      // const pos =
    },
    fitPhoto() {
      // this.pipeline.rotate = 0;
      this.pipeline.zoom = 1;
      this.pipeline.translate_x = 0;
      this.pipeline.translate_y = 0;
      this.zoomRanges[this.selectedFormat] = [];

      this.$nextTick(() => {
        const image_size = {
          x: this.canvasPresentation.present_width,
          y: this.canvasPresentation.present_height,
        };
        const frame_size = this.frameSizes[this.selectedFormat];

        if (image_size.x >= image_size.y) {
          let height = image_size.y;

          this.pipeline.zoom = 1 / ((height * 0.995) / frame_size[1]);

          const presentation = this.$refs.canvasContainer.computePresentation(
            this.pipeline.translate_x,
            this.pipeline.translate_y,
            this.pipeline.zoom,
            this.pipeline.rotate
          );

          let newVector = {
            x: presentation.frame_x,
            y: presentation.frame_y,
          };

          if (presentation.present_width < frame_size[0]) {
            this.pipeline.zoom = 1 / (image_size.x / frame_size[0]);
            newVector.y = 0;
          }

          // const inverseMatrix = this.$refs.canvasContainer.computeInverseMatrix(
          //   this.pipeline.translate_x,
          //   this.pipeline.translate_y,
          //   this.pipeline.zoom,
          //   0
          // );
          // const inverseMatrix = inverse(
          //   fromObject(this.$refs.canvasContainer.getLastMatrix())
          // );

          // let newVector = {
          //   x: presentation.frame_x,
          //   y: presentation.frame_y - (presentation.frame_height / 2 - presentation.present_height /2 ),
          // };

          const inverseMatrix = inverse(
            this.$refs.canvasContainer.computeMatrix(
              this.pipeline.translate_x,
              this.pipeline.translate_y,
              this.pipeline.zoom,
              0,
              this.isOrientationChanged ? image_size.y : image_size.x,
              this.isOrientationChanged ? image_size.x : image_size.y
            )
          );

          newVector = applyToPoint(inverseMatrix, newVector);
          // newVector2 = applyToPoint(inverseMatrix, newVector2);

          // const pairValues = this.isOrientationChanged
          //   ? [newVector.x, newVector2.x]
          //   : [newVector.y, newVector2.y];

          // console.log(newVector, newVector2);

          //  this.pipeline.translate_x = this.isOrientationChanged
          //   ? 0
          //   : newVector.x;
          // this.pipeline.translate_y = newVector.y;
          console.log(`widht fit:`, newVector);
          this.pipeline.translate_y = newVector.y;
        } else {
          let width = image_size.x;

          this.pipeline.zoom = 1 / ((width * 0.995) / frame_size[0]);

          const presentation = this.$refs.canvasContainer.computePresentation(
            this.pipeline.translate_x,
            this.pipeline.translate_y,
            this.pipeline.zoom,
            this.pipeline.rotate
          );

          if (presentation.present_height < frame_size[1]) {
            this.pipeline.zoom = 1 / (image_size.y / frame_size[1]);
          }

          const inverseMatrix = inverse(
            this.$refs.canvasContainer.computeMatrix(
              this.pipeline.translate_x,
              this.pipeline.translate_y,
              this.pipeline.zoom,
              0,
              this.isOrientationChanged ? image_size.y : image_size.x,
              this.isOrientationChanged ? image_size.x : image_size.y
            )
          );

          let newVector = {
            x: presentation.frame_x,
            y:
              presentation.frame_y -
              presentation.present_height / 2 +
              presentation.frame_height / 2,
          };

          newVector = applyToPoint(inverseMatrix, newVector);

          this.pipeline.translate_x = this.isOrientationChanged
            ? 0
            : newVector.x;
          this.pipeline.translate_y = newVector.y;
        }

        const rangeArray = this.zoomRanges[this.selectedFormat];

        if (!rangeArray.length) {
          rangeArray.push(Math.round(this.pipeline.zoom * 100));
          rangeArray.push(Math.round(this.pipeline.zoom * 3 * 100));
        }

        this.dirtyTempZoomValue = true;

        this.tempZoomValue = Math.round(this.pipeline.zoom * 100);

        this.fitPositions[this.selectedFormat] = [
          this.pipeline.translate_x,
          this.pipeline.translate_y,
        ];

        console.log(
          `Refitted photo of '${this.selectedFormat}' to ${this.pipeline.translate_x}, ${this.pipeline.translate_y}`
        );
      });
    },
    onRotate(direction) {
      if (direction == "left") {
        this.pipeline.rotate -= 90;
      } else {
        this.pipeline.rotate += 90;
      }

      if (this.pipeline.rotate > 360) this.pipeline.rotate = 90;
      if (this.pipeline.rotate < 0) this.pipeline.rotate = 270;

      this.$nextTick(this.fitPhoto);
    },
    chooseFormat(format, for_saving) {
      console.log(`Switching to format '${format}'`);
      const pipeline = {
        ...this.pipeline,
      };

      const presentation = this.canvasPresentation;

      pipeline.relative_x = presentation.frame_x - presentation.present_x;
      pipeline.relative_y = presentation.frame_y - presentation.present_y;

      pipeline.relative_width = presentation.present_width;
      pipeline.relative_height = presentation.present_height;

      this.pipeline = pipeline;

      const has_saved_format = !!this.saved_formats[format];

      this.saved_formats[this.selectedFormat] = {
        ...this.pipeline,
      };

      this.pipeline = Object.assign(
        {},
        this.saved_formats[format] || {
          rotate: 0,
          zoom: 1,
          translate_y: 0,
          translate_x: 0,
          relative_x: 0,
          relative_y: 0,
        }
      );

      this.selectedFormat = format;

      this.dirtyTempZoomValue = true;

      this.tempZoomValue = this.pipeline.zoom * 100;
      this.lastMovedZoomValue = this.pipeline.zoom;

      if (!has_saved_format && !for_saving) {
        if (this.cachedPipelines[format]) {
          this.pipeline = this.cachedPipelines[format];
        } else {
          console.log(`Format ${format} not saved, fitting photo`);
          this.$nextTick(this.fitPhoto);
        }
      }

      console.log(`Now viewing '${format}' image`);
    },
    onMoving(ev) {
      if (!this.isGrabbed) return;
      const dx = ev.movementX;
      const dy = ev.movementY;

      const presentation = this.$refs.canvasContainer.computePresentation(
        this.pipeline.translate_x + dx,
        this.pipeline.translate_y + dy,
        this.pipeline.zoom,
        this.pipeline.rotate
      );

      const [x_crop_valid, y_crop_valid] = this.checkCropping(
        dx,
        dy,
        presentation
      );

      if (x_crop_valid) {
        this.pipeline.translate_x += dx;
      }

      if (y_crop_valid) {
        this.pipeline.translate_y += dy;
      }

      this.lastMovedZoomValue = this.pipeline.zoom;
    },
    updateImageSize() {},
    onNewImageUploaded(sizes) {
      this.cachedPipelines = {
        standart: null,
        wide: null,
        ultrawide: null,
      };
      this.currentImageUrl = sizes.original;
    },
    fitToTop() {
      const wrapper = this.$refs.imgWrapper;
      const crop = this.$refs.imgPreview;

      const image_rect = wrapper.getBoundingClientRect();
      const crop_rect = crop.getBoundingClientRect();

      this.pipeline.translate_x += crop_rect.left - image_rect.left;
      this.pipeline.translate_y += crop_rect.top - image_rect.top;
    },
    onConfirm() {
      this.busy = true;
      this.chooseFormat(this.selectedFormat, true);

      MediaAPI.editPhoto(
        this.currentImageUrl,
        this.saved_formats.standart || this.getDefaultPipeline(),
        this.saved_formats.wide || this.getDefaultPipeline()
      )
        .then((data) => {
          MediaAPI.setLastPipelines(this.currentImageUrl, this.saved_formats);

          this.$emit("success", {
            ...data,
            original_url: this.currentImageUrl
          });
        })
        .catch(alert)
        .finally(() => {
          this.busy = false;
        });
    },
    checkCropping(_dx, _dy, _presentation) {
      const result = [false, false];

      const presentation = _presentation || this.canvasPresentation;

      const x = presentation.present_x;
      const y = presentation.present_y;

      const ex = presentation.present_x + presentation.present_width;
      const ey = presentation.present_y + presentation.present_height;

      const fx = presentation.frame_x + presentation.frame_width;
      const fy = presentation.frame_y + presentation.frame_height;

      if (x <= presentation.frame_x && ex >= fx) {
        result[0] = true;
      }

      if (y <= presentation.frame_y && ey >= fy) {
        result[1] = true;
      }

      return result;
    },
    setNearestValidImagePosition() {
      this.$nextTick(() => {
        const presentation = this.$refs.canvasContainer.computePresentation(
          this.pipeline.translate_x,
          this.pipeline.translate_y,
          this.pipeline.zoom,
          0
        );

        const inverseMatrix = inverse(
          fromObject(this.$refs.canvasContainer.getLastMatrix())
        );

        const image_x = presentation.present_x;
        const image_y = presentation.present_y;
        const frame_x = presentation.frame_x;
        const frame_y = presentation.frame_y;
        const image_width = presentation.present_width;
        const image_height = presentation.present_height;
        const frame_width = presentation.frame_width;
        const frame_height = presentation.frame_height;

        const correctionVector = { x: image_x, y: image_y };

        if (image_x > frame_x) {
          correctionVector.x = frame_x;
        } else if (image_x + image_width < frame_x + frame_width) {
          correctionVector.x = frame_x + frame_width - image_width;
        }

        if (image_y > frame_y) {
          correctionVector.y = frame_y;
        } else if (image_y + image_height < frame_y + frame_height) {
          correctionVector.y = frame_y + frame_height - image_height;
        }

        const absoluteCorrectionVector = applyToPoint(
          inverseMatrix,
          correctionVector
        );

        // const absoluteCorrectionVector = {
        //   x: correctionVector.x
        // };

        console.log(
          `Applying correction vectors:`,
          correctionVector,
          absoluteCorrectionVector
        );

        this.pipeline.translate_x = absoluteCorrectionVector.x;
        this.pipeline.translate_y = absoluteCorrectionVector.y;
      });
    },
    initialFit() {
      this.canvasReady = false;
      this.fitPositions = {
        standart: {},
        wide: {},
      };
      this.saved_formats = {
        standart: null,
        wide: null,
        ultrawide: null,
      };

      this.pipeline.rotate = 0;

      this.$nextTick(() => {
        this.chooseFormat("standart");

        this.$nextTick(() => {
          this.$nextTick(() => {
            setTimeout(() => {
              this.chooseFormat("wide");

              setTimeout(() => {
                if (!this.cachedPipelines[this.selectedFormat]) this.fitPhoto();
              }, 250);
              this.canvasReady = true;
            }, 550);
          });
        });
      });
    },
  },
  mounted() {
    const new_image_url = this.image_url
      .replace("_widescreen", "")
      .replace("_identity", "");

    MediaAPI.getLastPipelines(new_image_url)
      .then((data) => {
        if (data.pipelines) {
          this.cachedPipelines = data.pipelines;
        }
      })
      .catch((err) => {})
      .finally(() => {
        this.currentImageUrl = new_image_url;
      });
  },
  computed: {
    transformations() {
      return {
        transform: `translate(${this.pipeline.translate_x}px, ${this.pipeline.translate_y}px)  scale(${this.pipeline.zoom}) rotate(${this.pipeline.rotate}deg) `,
      };
    },
    movingCropStyles() {
      return {
        transform: `translate(${this.pipeline.translate_x}px,  ${this.pipeline.translate_y}px) scale(${this.pipeline.zoom})  rotate(${this.pipeline.rotate}deg) `,
      };
    },
    isOrientationChanged() {
      return this.pipeline.rotate == 90 || this.pipeline.rotate == 270;
    },
  },
};
</script>

<style scoped>
.editor-titles {
  display: flex;
  justify-content: space-between;
}

.modal-container {
  width: 1000px;
}

.editor-area,
.image-actions-area {
  display: flex;
}

.crop-frame {
  position: absolute;
  width: 240px;
  height: 240px;
  overflow: hidden;
  left: 0;
  right: 0;
  top: 72px;
  bottom: 0;
  margin: 0 auto;
  background: var(--white-color);
  z-index: 1;
  border: 2px solid var(--white-color);
  cursor: grabbing;
}

.crop-frame.wide {
  height: 240px;
  width: 426px;
}

.crop-frame.wide img {
  left: -144px;
}

.crop-frame.ultrawide {
  height: 200px;
  width: 710px;
}

.crop-frame.crop-frame.ultrawide img {
  left: -2px;
}

.crop-frame img {
  position: relative;
  left: -237px;
  top: -74px;
  /* height: 380px; */
  /* width: 714px; */
  /* width: 100%;
  height: auto; */
  opacity: 1 !important;
}

.image-area {
  position: relative;
  height: 100%;
}

.placeholder-box {
  /* width: 100%; */
  /* height: 100%; */
  background: var(--white-color);
  overflow: hidden;
  border-radius: 8px;
  position: relative;
  cursor: grabbing;

  width: 714px;
  height: 380px;
}

.image-area img.background {
  /* width: 714px; */
  /* height: 380px; */
  width: 100%;
  height: auto;
}

.black-backdrop {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0.52;
  width: 714px;
  height: 384px;
  background: var(--black-color);
}

.formats-area {
  width: 30%;
  justify-content: space-between;
}

.formats-actions-area {
  position: relative;
}

.actions-area {
  width: 100%;
  display: flex;
  flex-flow: row;
}

.editor-modal-button {
  width: 200px;
  max-width: 200px;
}

.editor-image-action {
  width: 64px;
  max-width: 64px;
}
</style>
